import {
  ApolloClient,
  HttpLink,
  InMemoryCache,
  split,
  from,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import { createClient } from "graphql-ws";
import { CLIENT_VERSION_HEADERS } from "./constants/client-version";
import { BASEURL } from "./utils/api";
import { publishCompatibilityError } from "./version-events";

const handleTokenExpiration = () => {
  const publicPaths = [
    "/sign-in",
    "/sign-up",
    "/verify-email",
    "/reset-password",
    "/forget-password",
    // "/",
    "/service-categories",
    "/services",
    "/verify-email",
    "/jobs",
  ];

  // Allow dynamic post, service detail, and job detail pages as public
  const isPublic =
    publicPaths.includes(window.location.pathname) ||
    /^\/view-post\/[\w-]+$/.test(window.location.pathname) ||
    /^\/services\/[\w-]+$/.test(window.location.pathname) ||
    /^\/jobs\/[\w-]+$/.test(window.location.pathname);
  if (isPublic) {
    return;
  }

  console.warn("🔴 Token expired. Redirecting to sign-in.");

  window.location.href = "/sign-in";
};

// 🔐 Auth error checker
const isAuthenticationError = (message: string, extensions: any) => {
  const authMessages = [
    "Token expired.",
    "Unauthorized",
    "Session expired. Please sign in again.",
  ];
  const authErrorCodes = ["UNAUTHENTICATED"];

  const hasAuthCode =
    extensions?.code && authErrorCodes.includes(extensions.code);
  const hasAuthMessage = message && authMessages.includes(message);

  return hasAuthCode || hasAuthMessage;
};

const errorLink = onError(({ graphQLErrors, operation }) => {
  const { skipAuthRedirect } = operation.getContext();
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, extensions }) => {
      const code = extensions?.code;
      if (code === "FORCE_UPDATE" || code === "MAINTENANCE") {
        publishCompatibilityError({
          code,
          message,
          updateUrl:
            typeof extensions?.updateUrl === "string"
              ? extensions.updateUrl
              : null,
        });
      } else if (isAuthenticationError(message, extensions)) {
        if (skipAuthRedirect) return;
        handleTokenExpiration();
      } else if (
        (extensions?.originalError as { code?: string } | undefined)?.code ===
        "SUBSCRIPTION_REQUIRED"
      ) {
        // Industry partner whose payment grace period ended: not an auth failure,
        // send them to the plans page where they can pay and continue.
        if (!window.location.pathname.startsWith("/plans")) {
          window.location.href = "/plans";
        }
      } else {
        console.log(`🟢 NON-AUTH error ignored: "${message}"`);
      }
    });
  }
});

const clientVersionLink = setContext((_, { headers }) => ({
  headers: {
    ...headers,
    ...CLIENT_VERSION_HEADERS,
  },
}));

// 🌐 HTTP Link
const httpLink = new HttpLink({
  uri: BASEURL,
  credentials: "include",
});

// Authenticated by the httpOnly `token` cookie sent with the upgrade request.
const wsLink = new GraphQLWsLink(
  createClient({
    // The server's WebSocket path is exactly `/graphql`; a trailing slash is rejected (close 1006).
    url: (import.meta.env.VITE_SOCKET_BASE_URL as string).replace(/\/+$/, ""),
    connectionParams: () => ({
      ...CLIENT_VERSION_HEADERS,
    }),
    on: {
      error: (error: any) => {
        console.error("🔌 WebSocket error:", error);
      },
    },
  }),
);

// Subscriptions bypass errorLink: a socket auth failure must not be treated as
// an expired HTTP session (which would redirect to sign-in).
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === "OperationDefinition" &&
      definition.operation === "subscription"
    );
  },
  wsLink,
  from([errorLink, clientVersionLink, httpLink]),
);

export const client = new ApolloClient({
  link: splitLink,
  cache: new InMemoryCache({
    typePolicies: {
      Conversation: {
        fields: {
          user: {
            merge(existing, incoming) {
              if (incoming == null && existing) return existing;
              return incoming;
            },
          },
          company: {
            merge(existing, incoming) {
              if (incoming == null && existing) return existing;
              return incoming;
            },
          },
        },
      },
      User: {
        fields: {
          title: {
            merge(existing, incoming) {
              if (incoming == null && existing) {
                return existing;
              }
              return incoming;
            },
          },
        },
      },
    },
  }),
});
